import classNames from 'classnames';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import { BotIcon } from '@/components/ui/bot-icon';
import { NotYet, type NotYetKey } from '@/components/ui/not-yet';
import { StageTag } from '@/components/ui/stage';
import type { LeadDetail } from '@/types/api';
import { formatDate, formatDateTime, initials, leadTitle, shownEmail } from '@/utils/format';
import { useApi } from '@/utils/use-api';

import { FollowUps, FollowUpsDemo } from './follow-ups';
import { CasinoList, DetailsList, Empty, FormAnswers, FormDemo, SourceBody, SummaryBody, SummaryDemo } from './lead-parts';
import { EditLeadPanel } from './new-lead';
import { EmailUpdatesControl } from './email-updates';
import { StageControl } from './stage-control';
import { TelegramLinkButton } from './telegram-link';
import { useClosing } from '@/utils/use-closing';

// `notYet`: it does not work in this version, so it shows faded, with the info mark at the end of its title row.
const Section = ({ title, aside, notYet, children }: { title: string; aside?: ReactNode; notYet?: NotYetKey; children: ReactNode }) => {
  const section = (
    <section className="border-t border-line px-4 py-4 md:px-5">
      <div className={classNames('mb-2 flex items-center justify-between gap-2', notYet && 'pr-7')}>
        <h3 className="text-xs font-medium uppercase tracking-wide text-fg-muted">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
  return notYet ? (
    <NotYet what={notYet} info="right-4 top-[18px] md:right-5">
      {section}
    </NotYet>
  ) : (
    section
  );
};

/**
 * Lead details beside the list: who, where from, what they asked, what we talked about,
 * which casinos. On a phone it takes the whole screen, above the top bar and the tabs (UI-1).
 */
const LeadPanel = ({ id, onClose }: { id: string; onClose: () => void }) => {
  const [closing, close] = useClosing(onClose);
  const { data, error, loading, reload } = useApi<LeadDetail>(`/leads/${encodeURIComponent(id)}`);
  // useApi keeps the last lead while the next one loads: never show it under another lead's row.
  const lead = data?.id === id ? data : null;
  const page = `/leads/${encodeURIComponent(id)}`;
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  return (
    <aside
      aria-label="Lead details"
      className={`fixed inset-y-0 right-0 z-40 flex w-full max-w-[440px] flex-col bg-surface-raised pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-e3 ${closing ? 'animate-drawer-out-right' : 'animate-drawer-in-right'}`}
    >
      <div className="flex items-start gap-3 py-4 pl-4 pr-2 md:px-5">
        {lead ? (
          <>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-inset text-sm text-fg-muted">
              {initials(lead.full_name, shownEmail(lead.email))}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-fg-strong">{leadTitle(lead.full_name, lead.email)}</p>
              <p className="truncate text-sm text-fg-muted">
                {lead.full_name ? shownEmail(lead.email) || 'No email' : ''}
                {lead.telegram_handle && <span>{lead.full_name ? ' · ' : ''}@{lead.telegram_handle}</span>}
              </p>
              <div className="mt-2">
                <StageTag stage={lead.stage} platform={lead.platform_stage} blocked={Boolean(lead.blocked_at)} />
                <span className="ml-2 text-xs text-fg-faint">since {formatDate(lead.stage_changed_at)}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 md:mt-2">
                <Link to={page} className="inline-flex min-h-11 items-center text-sm text-brand hover:underline md:min-h-0">
                  Open full page
                </Link>
                <button type="button" onClick={() => setEditing(true)} className="inline-flex min-h-11 items-center text-sm text-brand hover:underline md:min-h-0">
                  Edit
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 text-sm text-fg-muted">{error ?? (loading ? 'Loading…' : '')}</div>
        )}
        <button
          type="button"
          onClick={close}
          aria-label="Close lead details"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-fg-muted hover:bg-surface-hover hover:text-fg md:h-8 md:w-8"
        >
          <svg viewBox="0 0 16 16" className="h-5 w-5 md:h-4 md:w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>

      {lead && (
        <div className={classNames('min-h-0 flex-1 overflow-y-auto transition-opacity', loading && 'opacity-60')}>
          <Section title="Stage">
            <StageControl lead={lead} onChange={reload} />
          </Section>

          <Section title="Email updates" notYet="email">
            <EmailUpdatesControl lead={lead} onChange={reload} />
          </Section>

          <Section title="Latest AI conversation summary" aside={<SummaryDemo lead={lead} />} notYet="ai">
            <SummaryBody lead={lead} />
          </Section>

          <Section title="Follow-ups" aside={<FollowUpsDemo lead={lead} />}>
            <FollowUps lead={lead} onChange={reload} />
          </Section>

          <Section title="Details">
            <DetailsList lead={lead} />
          </Section>

          <Section title="Source">
            <SourceBody lead={lead} />
          </Section>

          <Section title="Contact form answers" aside={<FormDemo lead={lead} />}>
            <FormAnswers lead={lead} />
          </Section>

          <Section title={`Casinos (${lead.casinos.length})`}>
            <CasinoList lead={lead} />
          </Section>

          <Section
            title="Message history"
            aside={
              lead.recent_messages.length > 0 && (
                <Link to={`${page}/conversation`} className="inline-flex min-h-11 items-center text-sm text-brand hover:underline md:min-h-0 md:text-xs">
                  Full conversation
                </Link>
              )
            }
          >
            {lead.recent_messages.length ? (
              <>
                <ol className="space-y-3">
                  {lead.recent_messages.map((m, i) => (
                    <li key={i} className="text-sm">
                      <p className="inline-flex items-center gap-1 text-xs text-fg-muted">
                        {m.from_bot && <BotIcon />}
                        <span className={m.from_team ? 'text-fg' : 'text-fg-strong'}>
                          {m.from_bot ? 'Prisma bot' : m.from_team ? `${m.from} (team)` : m.from}
                        </span>
                        <span className="ml-1 text-fg-faint">{formatDateTime(m.sent_at)}</span>
                      </p>
                      <p
                        className={classNames(
                          'mt-0.5 break-words rounded-lg px-2.5 py-1.5 text-fg',
                          m.from_bot ? 'bg-info/15' : m.from_team ? 'bg-brand/10' : 'bg-surface-inset',
                        )}
                      >
                        {m.text}
                      </p>
                    </li>
                  ))}
                </ol>
                <p className="mt-3 text-xs text-fg-faint">
                  Latest {lead.recent_messages.length} of {lead.chats.reduce((n, c) => n + c.messages, 0)} messages in{' '}
                  {lead.chats.map((c) => c.title).join(', ')}.
                </p>
              </>
            ) : (
              <Empty>No Telegram group linked to this lead yet (TG-1, TG-2).</Empty>
            )}
            <div className="mt-3">
              <TelegramLinkButton leadId={lead.id} linked={lead.telegram_linked} />
            </div>
          </Section>
        </div>
      )}
      {/* On the body: the panel's slide-in leaves a transform, which would hold the dialog inside the panel and leave the list clickable. */}
      {editing &&
        lead &&
        createPortal(
          <EditLeadPanel
            lead={lead}
            onClose={() => setEditing(false)}
            onSaved={() => {
              setEditing(false);
              reload();
            }}
          />,
          document.body,
        )}
    </aside>
  );
};

export default LeadPanel;
